import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import { useSpotify } from '../context/SpotifyContext';
import { Button } from '../components/shared/Button';
import { YouTubeSearch } from '../components/YouTube/YouTubeSearch';
import { SpotifySearch } from '../components/Spotify/SpotifySearch';
import { DiscogsVerify } from '../components/Discogs';
import { handleImageUpload, handleAudioUpload, validateAudioFile, validateImageFile } from '../utils/fileHandlers';
import './Prepare.css';

// API base URL
const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : 'http://localhost:3001/api';

interface SavedPlaylist {
  id: number;
  name: string;
  songCount: number;
  songsPerRound: number;
  created_at: string;
  updated_at: string;
}

type SearchSource = 'youtube' | 'spotify' | 'both' | 'manual';

const Prepare: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDjDeck = location.pathname === '/dj-deck';
  const { playlist, addSong, removeSong, updateSong, updateSongQuestions, clearPlaylist } = useGame();
  
  // Audio preview hooks
  const { play, stop, isPlaying: audioIsPlaying } = useAudioPlayer();
  const { 
    isAuthenticated: spotifyAuthenticated, 
    isPremium: spotifyPremium, 
    isReady: spotifyReady,
    play: spotifyPlay,
    pause: spotifyPause,
    isPlaying: spotifyIsPlaying
  } = useSpotify();
  
  const [previewingSongId, setPreviewingSongId] = useState<string | null>(null);
  const [searchSource, setSearchSource] = useState<SearchSource>('youtube');
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [year, setYear] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [error, setError] = useState('');
  const [editingQuestionsSongId, setEditingQuestionsSongId] = useState<string | null>(null);
  const [editingLyricsSongId, setEditingLyricsSongId] = useState<string | null>(null);
  const [editingSongId, setEditingSongId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editArtist, setEditArtist] = useState('');
  const [editYear, setEditYear] = useState('');
  const [editYearSource, setEditYearSource] = useState('');
  const [newQuestion, setNewQuestion] = useState('');
const [editLyrics, setEditLyrics] = useState('');
  const [editLyricsSpeed, setEditLyricsSpeed] = useState<number>(2);
  const [editLyricsOffsetSeconds, setEditLyricsOffsetSeconds] = useState<number>(0);
  const [editShowLyrics, setEditShowLyrics] = useState<boolean>(true);

  // Saved playlists state
  const [savedPlaylists, setSavedPlaylists] = useState<SavedPlaylist[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [savePlaylistName, setSavePlaylistName] = useState('');
  const [savingSongsPerRound, setSavingSongsPerRound] = useState(10);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  const [savingPlaylist, setSavingPlaylist] = useState(false);

  const audioInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Fetch saved playlists
  const fetchSavedPlaylists = async () => {
    setLoadingPlaylists(true);
    try {
      const response = await fetch(`${API_URL}/saved-playlists`);
      if (response.ok) {
        const data = await response.json();
        setSavedPlaylists(data);
      }
    } catch (err) {
      console.error('Error fetching saved playlists:', err);
    } finally {
      setLoadingPlaylists(false);
    }
  };

  // Load playlists when modal opens
  useEffect(() => {
    if (showLoadModal) {
      fetchSavedPlaylists();
    }
  }, [showLoadModal]);

  // Save current playlist
  const handleSavePlaylist = async () => {
    if (!savePlaylistName.trim() || playlist.length === 0) return;
    
    setSavingPlaylist(true);
    try {
      const response = await fetch(`${API_URL}/saved-playlists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: savePlaylistName.trim(),
          songs: playlist,
          songsPerRound: savingSongsPerRound
        })
      });
      
      if (response.ok) {
        setShowSaveModal(false);
        setSavePlaylistName('');
        alert('Playlist opgeslagen!');
      } else {
        const data = await response.json();
        alert(`Fout: ${data.error || 'Kon playlist niet opslaan'}`);
      }
    } catch (err) {
      console.error('Error saving playlist:', err);
      alert('Fout bij opslaan playlist');
    } finally {
      setSavingPlaylist(false);
    }
  };

  // Load a saved playlist
  const handleLoadPlaylist = async (playlistId: number) => {
    try {
      const response = await fetch(`${API_URL}/saved-playlists/${playlistId}`);
      if (response.ok) {
        const data = await response.json();
        // Clear current playlist and add loaded songs
        clearPlaylist();
        data.songs.forEach((song: any) => {
          addSong({
            title: song.title,
            artist: song.artist,
            year: song.year,
            audioUrl: song.audioUrl,
            imageUrl: song.imageUrl,
            youtubeVideoId: song.youtubeVideoId || song.youtubeId,
            lyrics: song.lyrics,
            lyricsSpeed: song.lyricsSpeed,
            lyricsOffsetSeconds: song.lyricsOffsetSeconds,
            showLyrics: song.showLyrics,
            questions: song.questions || []
          });
        });
        setShowLoadModal(false);
        alert(`Playlist "${data.name}" geladen met ${data.songs.length} nummers`);
      }
    } catch (err) {
      console.error('Error loading playlist:', err);
      alert('Fout bij laden playlist');
    }
  };

  // Delete a saved playlist
  const handleDeletePlaylist = async (playlistId: number, playlistName: string) => {
    if (!window.confirm(`Weet je zeker dat je "${playlistName}" wilt verwijderen?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/saved-playlists/${playlistId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        fetchSavedPlaylists();
      }
    } catch (err) {
      console.error('Error deleting playlist:', err);
    }
  };

  // Preview song handler
  const handlePreview = async (songId: string, songAudioUrl: string, spotifyUri?: string) => {
    // If already previewing this song, stop it
    if (previewingSongId === songId) {
      stop();
      if (spotifyUri && spotifyAuthenticated && spotifyPremium) {
        await spotifyPause();
      }
      setPreviewingSongId(null);
      return;
    }
    
    // Stop any current preview
    stop();
    if (spotifyAuthenticated && spotifyPremium) {
      await spotifyPause();
    }
    
    // Start new preview
    setPreviewingSongId(songId);
    
    // Use Spotify if available and song has spotifyUri
    if (spotifyUri && spotifyAuthenticated && spotifyPremium && spotifyReady) {
      await spotifyPlay(spotifyUri);
    } else if (songAudioUrl) {
      // Use HTML audio player
      play(songAudioUrl);
    }
  };

  const handleAudioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (validateAudioFile(file)) {
        handleAudioUpload(file, (url) => {
          setAudioUrl(url);
          setAudioLoaded(true);
          setError('');
        });
      } else {
        setError('Ongeldig audio formaat. Gebruik MP3, WAV of OGG.');
      }
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (validateImageFile(file)) {
        handleImageUpload(file, (dataUrl) => {
          setImageUrl(dataUrl);
          setError('');
        });
      } else {
        setError('Ongeldig afbeelding formaat of te groot (max 5MB).');
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim() || !artist.trim() || !year || !audioUrl) {
      setError('Vul alle verplichte velden in.');
      return;
    }

    const yearNum = parseInt(year);
    if (isNaN(yearNum) || yearNum < 1900 || yearNum > new Date().getFullYear()) {
      setError('Voer een geldig jaartal in (1900 - heden).');
      return;
    }

    addSong({
      title: title.trim(),
      artist: artist.trim(),
      year: yearNum,
      audioUrl,
      imageUrl: imageUrl || undefined,
    });

    // Reset form
    setTitle('');
    setArtist('');
    setYear('');
    setAudioUrl('');
    setAudioLoaded(false);
    setImageUrl('');
    if (audioInputRef.current) audioInputRef.current.value = '';
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleYouTubeDownload = (song: {
    title: string;
    artist: string;
    year: number;
    audioUrl: string;
    imageUrl?: string;
    youtubeVideoId?: string;
  }) => {
    addSong({
      title: song.title,
      artist: song.artist,
      year: song.year,
      audioUrl: song.audioUrl,
      imageUrl: song.imageUrl,
      youtubeVideoId: song.youtubeVideoId,
    });
  };

  const handleSpotifySong = (song: {
    title: string;
    artist: string;
    year: number;
    youtubeUrl?: string;
    imageUrl?: string;
    previewUrl?: string;
    spotifyUri?: string;
  }) => {
    // If we have a Spotify URI, add with Spotify playback support
    if (song.spotifyUri) {
      addSong({
        title: song.title,
        artist: song.artist,
        year: song.year,
        audioUrl: '', // No direct audio URL - will use Spotify Web Playback SDK
        spotifyUri: song.spotifyUri,
        imageUrl: song.imageUrl,
      });
    } else if (song.previewUrl) {
      // If we have a preview URL we can use it directly
      addSong({
        title: song.title,
        artist: song.artist,
        year: song.year,
        audioUrl: song.previewUrl,
        imageUrl: song.imageUrl,
      });
    } else if (song.youtubeUrl) {
      // Trigger YouTube download for the selected URL
      // This will be handled by the YouTubeSearch component's download flow
      // For now, we'll pre-fill the form and let user confirm
      setTitle(song.title);
      setArtist(song.artist);
      setYear(song.year.toString());
      setImageUrl(song.imageUrl || '');
      setSearchSource('youtube');
      // Note: User should search on YouTube tab to download
    }
  };

  return (
    <div className="prepare-page">
      <div className="prepare-container">
        <div className="prepare-header">
          <h1>{isDjDeck ? '🎛️ DJ Deck' : '🎵 Playlist Voorbereiden'}</h1>
          <button 
            className="dashboard-link"
            onClick={() => navigate(isDjDeck ? '/spotify-dashboard' : '/dj-deck')}
          >
            {isDjDeck ? '🎧 Music Dashboard →' : '🎛️ DJ Deck →'}
          </button>
        </div>

        <div className="prepare-content">
          <div className="form-section">
            {/* Source tabs */}
            <div className="source-tabs">
              <button 
                className={`source-tab ${searchSource === 'youtube' ? 'active' : ''}`}
                onClick={() => setSearchSource('youtube')}
              >
                🎬 YouTube
              </button>
              <button 
                className={`source-tab spotify ${searchSource === 'spotify' ? 'active' : ''}`}
                onClick={() => setSearchSource('spotify')}
              >
                🎧 Spotify
              </button>
              <button
                className={`source-tab ${searchSource === 'both' ? 'active' : ''}`}
                onClick={() => setSearchSource('both')}
              >
                🔀 Beide
              </button>
              <button 
                className={`source-tab ${searchSource === 'manual' ? 'active' : ''}`}
                onClick={() => setSearchSource('manual')}
              >
                📁 Handmatig
              </button>
            </div>

            {/* YouTube Search */}
            {searchSource === 'youtube' && (
              <YouTubeSearch onSongDownloaded={handleYouTubeDownload} />
            )}

            {/* Spotify Search */}
            {searchSource === 'spotify' && (
              <SpotifySearch onAddSong={handleSpotifySong} />
            )}

            {/* Combined search */}
            {searchSource === 'both' && (
              <>
                <YouTubeSearch onSongDownloaded={handleYouTubeDownload} />
                <div style={{ height: 12 }} />
                <SpotifySearch onAddSong={handleSpotifySong} />
              </>
            )}

            {/* Manual Upload */}
            {searchSource === 'manual' && (
            <div className="manual-upload-section">
              <h2>📁 Handmatig Toevoegen</h2>
              <form onSubmit={handleSubmit} className="song-form">
              <div className="form-group">
                <label htmlFor="title">Titel *</label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Bijv. Bohemian Rhapsody"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="artist">Artiest *</label>
                <input
                  type="text"
                  id="artist"
                  value={artist}
                  onChange={(e) => setArtist(e.target.value)}
                  placeholder="Bijv. Queen"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="year">Jaartal *</label>
                <input
                  type="number"
                  id="year"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  placeholder="Bijv. 1975"
                  min="1900"
                  max={new Date().getFullYear()}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="audio">Audio Bestand * (MP3, WAV, OGG)</label>
                <input
                  type="file"
                  id="audio"
                  ref={audioInputRef}
                  accept="audio/mpeg,audio/wav,audio/ogg,audio/mp3"
                  onChange={handleAudioChange}
                  required={!audioLoaded}
                />
                {audioLoaded && <span className="success-indicator">✓ Audio geladen</span>}
              </div>

              <div className="form-group">
                <label htmlFor="image">Afbeelding (optioneel)</label>
                <input
                  type="file"
                  id="image"
                  ref={imageInputRef}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageChange}
                />
                {imageUrl && (
                  <div className="image-preview">
                    <img src={imageUrl} alt="Preview" />
                  </div>
                )}
              </div>

              {error && <div className="error-message">{error}</div>}

              <Button type="submit" variant="primary" fullWidth>
                + Toevoegen aan Playlist
              </Button>
              </form>
            </div>
            )}
          </div>

          <div className="playlist-section">
            <div className="playlist-header">
              <h2>Playlist ({playlist.length} nummers)</h2>
              {playlist.length > 0 && (
                <Button
                  variant="danger"
                  size="small"
                  onClick={() => {
                    if (window.confirm('Weet je zeker dat je de hele playlist wilt wissen?')) {
                      clearPlaylist();
                    }
                  }}
                >
                  🗑️ Wis Playlist
                </Button>
              )}
            </div>
            
            {/* Discogs verificatie */}
            {playlist.length > 0 && (
              <DiscogsVerify 
                songs={playlist} 
                onUpdateSong={(songId, updates) => updateSong(songId, updates)}
              />
            )}
            
            {playlist.length === 0 ? (
              <div className="empty-playlist">
                <p>Nog geen nummers toegevoegd</p>
                <p>Voeg nummers toe via het formulier</p>
              </div>
            ) : (
              <div className="playlist-list">
                {playlist.map((song, index) => (
                  <div
                    key={song.id}
                    className={`song-card ${song.played ? 'played' : ''} ${editingQuestionsSongId === song.id ? 'editing' : ''} ${editingSongId === song.id ? 'editing-details' : ''}`}
                  >
                    <div className="song-thumbnail">
                      {song.imageUrl ? (
                        <img src={song.imageUrl} alt={song.title} />
                      ) : (
                        <div className="no-image">🎵</div>
                      )}
                    </div>
                    <div className="song-info" onClick={() => {
                      if (editingSongId !== song.id) {
                        setEditingSongId(song.id);
                        setEditTitle(song.title);
                        setEditArtist(song.artist);
                        setEditYear(song.year.toString());
                        setEditYearSource(song.yearSource || '');
                      }
                    }} style={{ cursor: editingSongId === song.id ? 'default' : 'pointer' }}>
                      {editingSongId === song.id ? (
                        <>
                          <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="edit-input edit-title-input"
                            placeholder="Titel"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="edit-row">
                            <input
                              type="text"
                              value={editArtist}
                              onChange={(e) => setEditArtist(e.target.value)}
                              className="edit-input edit-artist-input"
                              placeholder="Artiest"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <input
                              type="number"
                              value={editYear}
                              onChange={(e) => setEditYear(e.target.value)}
                              className="edit-input edit-year-input"
                              placeholder="Jaar"
                              min="1900"
                              max={new Date().getFullYear()}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <input
                            type="text"
                            value={editYearSource}
                            onChange={(e) => setEditYearSource(e.target.value)}
                            className="edit-input edit-year-source-input"
                            placeholder="🔗 Bron voor het jaar (bijv. Wikipedia URL)"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="edit-actions" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="success"
                              size="small"
                              onClick={() => {
                                if (editTitle.trim() && editArtist.trim() && editYear) {
                                  updateSong(song.id, {
                                    title: editTitle.trim(),
                                    artist: editArtist.trim(),
                                    year: parseInt(editYear),
                                    yearSource: editYearSource.trim() || undefined
                                  });
                                  setEditingSongId(null);
                                }
                              }}
                            >
                              ✓ Opslaan
                            </Button>
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                setEditingSongId(null);
                              }}
                            >
                              ✕ Annuleer
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="song-title">
                            {index + 1}. {song.title}
                          </div>
                          <div className="song-artist">
                            {song.artist} • {song.year}
                            {song.yearSource && <span className="year-source-indicator" title={song.yearSource}> 🔗</span>}
                          </div>
                          <div className="song-questions-count">
                            ❓ {song.questions?.length || 0} vragen
                          </div>
                        </>
                      )}
                    </div>
                    <div className="song-actions">
                      {/* Preview button */}
                      {(song.audioUrl || song.spotifyUri) && (
                        <button
                          className={`preview-btn ${previewingSongId === song.id ? 'playing' : ''}`}
                          onClick={() => handlePreview(song.id, song.audioUrl, song.spotifyUri)}
                          aria-label="Preview nummer"
                          title={song.spotifyUri ? 'Speel via Spotify' : 'Speel preview'}
                        >
                          {previewingSongId === song.id ? '⏹️' : (song.spotifyUri ? '🎧' : '▶️')}
                        </button>
                      )}
                      {/* Video toggle button */}
                      {song.youtubeVideoId && (
                        <button
                          className={`video-toggle-btn ${song.showVideo ? 'active' : ''}`}
                          onClick={() => updateSong(song.id, { showVideo: !song.showVideo })}
                          aria-label="Toggle video"
                          title={song.showVideo ? 'Video uitschakelen' : 'Video inschakelen'}
                        >
                          {song.showVideo ? '📺' : '📺'}
                        </button>
                      )}
                      {song.played && <span className="played-badge">✓ Gespeeld</span>}
                      <button
                        className="edit-questions-btn"
                        onClick={() => setEditingQuestionsSongId(
                          editingQuestionsSongId === song.id ? null : song.id
                        )}
                        aria-label="Bewerk vragen"
                      >
                        ❓
                      </button>
                      <button
                        className={`edit-lyrics-btn ${song.lyrics ? 'has-lyrics' : ''} ${song.lyrics && song.showLyrics === false ? 'lyrics-hidden' : ''}`}
                        onClick={() => {
                          if (editingLyricsSongId === song.id) {
                            setEditingLyricsSongId(null);
                          } else {
                            setEditingLyricsSongId(song.id);
                            setEditLyrics(song.lyrics || '');
                            setEditLyricsSpeed(song.lyricsSpeed || 2);
                            setEditLyricsOffsetSeconds(song.lyricsOffsetSeconds ?? 0);
                            setEditShowLyrics(song.showLyrics !== false);
                          }
                        }}
                        aria-label="Bewerk karaoketekst"
                        title="Karaoketekst bewerken"
                      >
                        🎤
                      </button>
                      <button
                        className="remove-song-btn"
                        onClick={() => removeSong(song.id)}
                        aria-label="Verwijder nummer"
                      >
                        ×
                      </button>
                    </div>

                    {editingQuestionsSongId === song.id && (
                      <div className="questions-editor">
                        <h4>Vragen voor dit nummer:</h4>
                        <div className="question-add-form">
                          <input
                            type="text"
                            value={newQuestion}
                            onChange={(e) => setNewQuestion(e.target.value)}
                            placeholder="Bijv. Welke artiest zingt dit?"
                            className="question-input"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && newQuestion.trim()) {
                                e.preventDefault();
                                updateSongQuestions(song.id, [...(song.questions || []), newQuestion.trim()]);
                                setNewQuestion('');
                              }
                            }}
                          />
                          <Button
                            variant="primary"
                            size="small"
                            onClick={() => {
                              if (newQuestion.trim()) {
                                updateSongQuestions(song.id, [...(song.questions || []), newQuestion.trim()]);
                                setNewQuestion('');
                              }
                            }}
                          >
                            +
                          </Button>
                        </div>
                        <div className="questions-list">
                          {(song.questions || []).map((q, qIndex) => (
                            <div key={qIndex} className="question-item">
                              <span className="question-number">{qIndex + 1}.</span>
                              <span className="question-text">{q}</span>
                              <button
                                className="remove-question-btn"
                                onClick={() => {
                                  const newQuestions = song.questions.filter((_, i) => i !== qIndex);
                                  updateSongQuestions(song.id, newQuestions);
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          {(!song.questions || song.questions.length === 0) && (
                            <p className="no-questions">Nog geen vragen toegevoegd</p>
                          )}
                        </div>
                      </div>
                    )}

                    {editingLyricsSongId === song.id && (
                      <div className="lyrics-editor">
                        <h4>🎤 Karaoketekst voor dit nummer:</h4>
                        <div className="lyrics-timing-help">
                          <strong>💡 Timing tips:</strong>
                          <ul>
                            <li><code>[0:30]</code> = toon op 0:30 minuten</li>
                            <li><code>[5s]</code> = wacht 5 seconden voor deze regel</li>
                          </ul>
                        </div>
                        <textarea
                          value={editLyrics}
                          onChange={(e) => setEditLyrics(e.target.value)}
                          placeholder="Voer de songtekst in met optionele timing...&#10;&#10;Voorbeeld met timing:&#10;[0:05]We will, we will rock you&#10;[0:10]We will, we will rock you&#10;[5s]Buddy you're a boy make a big noise&#10;Playing in the street..."
                          className="lyrics-textarea"
                          rows={12}
                        />
                        <div className="lyrics-show-toggle">
                          <label className="lyrics-show-label">
                            <input
                              type="checkbox"
                              checked={editShowLyrics}
                              onChange={(e) => setEditShowLyrics(e.target.checked)}
                            />
                            Songtekst tonen op displayscherm
                          </label>
                        </div>
                        <div className="lyrics-speed-control">
                          <label>Basis scrollsnelheid (voor regels zonder timing):</label>
                          <div className="speed-buttons">
                            <button
                              className={`speed-btn ${editLyricsSpeed === 1 ? 'active' : ''}`}
                              onClick={() => setEditLyricsSpeed(1)}
                              type="button"
                            >
                              🐢 Langzaam
                            </button>
                            <button
                              className={`speed-btn ${editLyricsSpeed === 2 ? 'active' : ''}`}
                              onClick={() => setEditLyricsSpeed(2)}
                              type="button"
                            >
                              🎵 Normaal
                            </button>
                            <button
                              className={`speed-btn ${editLyricsSpeed === 3 ? 'active' : ''}`}
                              onClick={() => setEditLyricsSpeed(3)}
                              type="button"
                            >
                              🚀 Snel
                            </button>
                          </div>
                        </div>
                        <div className="lyrics-speed-control">
                          <label>Tekstvertraging op display (per nummer):</label>
                          <div className="speed-buttons">
                            <button
                              className={`speed-btn ${editLyricsOffsetSeconds === -1 ? 'active' : ''}`}
                              onClick={() => setEditLyricsOffsetSeconds(-1)}
                              type="button"
                            >
                              -1s
                            </button>
                            <button
                              className={`speed-btn ${editLyricsOffsetSeconds === -0.5 ? 'active' : ''}`}
                              onClick={() => setEditLyricsOffsetSeconds(-0.5)}
                              type="button"
                            >
                              -0.5s
                            </button>
                            <button
                              className={`speed-btn ${editLyricsOffsetSeconds === 0 ? 'active' : ''}`}
                              onClick={() => setEditLyricsOffsetSeconds(0)}
                              type="button"
                            >
                              0s
                            </button>
                            <button
                              className={`speed-btn ${editLyricsOffsetSeconds === 0.5 ? 'active' : ''}`}
                              onClick={() => setEditLyricsOffsetSeconds(0.5)}
                              type="button"
                            >
                              +0.5s
                            </button>
                            <button
                              className={`speed-btn ${editLyricsOffsetSeconds === 1 ? 'active' : ''}`}
                              onClick={() => setEditLyricsOffsetSeconds(1)}
                              type="button"
                            >
                              +1s
                            </button>
                          </div>
                        </div>
                        <div className="lyrics-actions">
                          <Button
                            variant="success"
                            size="small"
                            onClick={() => {
                              const hasLyrics = editLyrics.trim().length > 0;
                              updateSong(song.id, { 
                                lyrics: hasLyrics ? editLyrics.trim() : undefined,
                                lyricsSpeed: editLyricsSpeed,
                                lyricsOffsetSeconds: hasLyrics ? editLyricsOffsetSeconds : undefined,
                                showLyrics: editShowLyrics
                              });
                              setEditingLyricsSongId(null);
                              setEditLyrics('');
                              setEditLyricsOffsetSeconds(0);
                            }}
                          >
                            ✓ Opslaan
                          </Button>
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={() => {
                              setEditingLyricsSongId(null);
                              setEditLyrics('');
                              setEditLyricsOffsetSeconds(0);
                            }}
                          >
                            ✕ Annuleren
                          </Button>
                          {song.lyrics && (
                            <Button
                              variant="danger"
                              size="small"
                              onClick={() => {
                                  updateSong(song.id, { lyrics: undefined, lyricsOffsetSeconds: undefined });
                                setEditingLyricsSongId(null);
                                setEditLyrics('');
                                  setEditLyricsOffsetSeconds(0);
                              }}
                            >
                              🗑️ Verwijderen
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="prepare-actions">
          <Button variant="secondary" onClick={() => navigate('/')}>
            ← Terug naar Teams
          </Button>
          <div className="playlist-db-actions">
            <Button 
              variant="secondary" 
              onClick={() => setShowLoadModal(true)}
            >
              📂 Laden
            </Button>
            <Button 
              variant="secondary" 
              onClick={() => {
                if (playlist.length === 0) {
                  alert('Voeg eerst nummers toe aan de playlist');
                  return;
                }
                setShowSaveModal(true);
              }}
            >
              💾 Opslaan
            </Button>
          </div>
          <Button
            variant="success"
            size="large"
            onClick={() => navigate('/admin')}
            disabled={playlist.length === 0}
          >
            Start Spel! ({playlist.length} nummers klaar) →
          </Button>
        </div>

        {/* Save Playlist Modal */}
        {showSaveModal && (
          <div className="modal-overlay" onClick={() => setShowSaveModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h2>💾 Playlist Opslaan</h2>
              <div className="modal-body">
                <div className="form-group">
                  <label>Naam:</label>
                  <input
                    type="text"
                    value={savePlaylistName}
                    onChange={(e) => setSavePlaylistName(e.target.value)}
                    placeholder="Bijv: Kerst 2026, Jaren 80, etc."
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>Nummers per ronde:</label>
                  <select 
                    value={savingSongsPerRound} 
                    onChange={(e) => setSavingSongsPerRound(Number(e.target.value))}
                  >
                    <option value={5}>5 nummers</option>
                    <option value={10}>10 nummers</option>
                    <option value={15}>15 nummers</option>
                    <option value={20}>20 nummers</option>
                  </select>
                </div>
                <p className="modal-info">
                  {playlist.length} nummers • {Math.ceil(playlist.length / savingSongsPerRound)} ronde(s)
                </p>
              </div>
              <div className="modal-actions">
                <Button variant="secondary" onClick={() => setShowSaveModal(false)}>
                  Annuleren
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleSavePlaylist}
                  disabled={!savePlaylistName.trim() || savingPlaylist}
                >
                  {savingPlaylist ? 'Opslaan...' : 'Opslaan'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Load Playlist Modal */}
        {showLoadModal && (
          <div className="modal-overlay" onClick={() => setShowLoadModal(false)}>
            <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
              <h2>📂 Playlist Laden</h2>
              <div className="modal-body">
                {loadingPlaylists ? (
                  <p className="loading-text">Laden...</p>
                ) : savedPlaylists.length === 0 ? (
                  <p className="no-playlists">Geen opgeslagen playlists gevonden</p>
                ) : (
                  <div className="saved-playlists-list">
                    {savedPlaylists.map(pl => (
                      <div key={pl.id} className="saved-playlist-item">
                        <div className="playlist-info">
                          <h4>{pl.name}</h4>
                          <p>
                            {pl.songCount} nummers • {pl.songsPerRound} per ronde • 
                            {Math.ceil(pl.songCount / pl.songsPerRound)} rondes
                          </p>
                          <span className="playlist-date">
                            {new Date(pl.updated_at).toLocaleDateString('nl-NL')}
                          </span>
                        </div>
                        <div className="playlist-actions">
                          <Button 
                            variant="primary" 
                            size="small"
                            onClick={() => handleLoadPlaylist(pl.id)}
                          >
                            Laden
                          </Button>
                          <Button 
                            variant="danger" 
                            size="small"
                            onClick={() => handleDeletePlaylist(pl.id, pl.name)}
                          >
                            🗑️
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <Button variant="secondary" onClick={() => setShowLoadModal(false)}>
                  Sluiten
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Prepare;
